import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { resolveViewerTimezone } from '@xemplo/date-time';
import { PayrollDynamicRoutes } from '@xemplo/gp-routes';
import {
  PayrollProcessingSystemId,
  PayrunListItem,
  PayrunSteps,
  StatusToStepMap,
} from '@xemplo/gp-types';
import { getStepStatusIds, transformStatus } from '@xemplo/gp-utils';
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The zone `MilestoneDueDateCell` formats the due date in. The day-diff must quantise in
 * the same zone or the label and the date beneath it disagree.
 */
const VIEWER_TZ = resolveViewerTimezone();

//The active table should only show payruns that are not completed or draft
const { [PayrunSteps.Complete]: _complete, ...activeSteps } = StatusToStepMap(true);
export const allActiveStepIds = Object.values(activeSteps).flat();

export const activeStatusFilter = transformStatus(allActiveStepIds);
/**
 * Expands selected milestones (steps) into their constituent payrun status ids,
 * de-duplicated. Falls back to the provided defaults when no milestone is selected.
 */

export function expandMilestonesToStatusIds(
  milestones: PayrunSteps[] | undefined,
  defaultStatusIds: number[],
): number[] {
  if (!milestones?.length) return defaultStatusIds;
  return Array.from(new Set(milestones.flatMap(getStepStatusIds)));
}

export type MilestoneDateDiff = {
  /** Calendar-day difference between today and the due date; negative = overdue, 0 = today. */
  days: number;
  /** Whether the due moment has already passed (now is after the due date-time). */
  isPast: boolean;
};
/**
 * @param payrun
 * @returns The calendar-day difference to the milestone due date and whether the due
 * moment has passed. Day boundaries are resolved in the viewer's timezone — the same zone
 * the cell formats the due date in — so "Due tomorrow" always agrees with the date printed
 * beneath it. The input is still *parsed* as UTC, so naive (`Z`-less) backend strings are
 * read correctly. `isPast` is an instant comparison and is zone-independent; it
 * disambiguates a milestone due earlier today (overdue) from one due later today.
 */
export function getDateDiffFromMilestone(payrun: PayrunListItem): MilestoneDateDiff {
  const dueUtc = dayjs.utc(payrun.milestoneDueDateTimeUtc);
  const days = dueUtc
    .tz(VIEWER_TZ)
    .startOf('day')
    .diff(dayjs().tz(VIEWER_TZ).startOf('day'), 'day');
  return { days, isPast: dayjs().isAfter(dueUtc) };
}

type MilestoneStepKey = PayrunSteps | 'Draft';

/**
 * @param payrun
 * @returns The current milestone of the payrun based on the payrunStatusId and whether an invoice is needed. Returns null if the milestone cannot be determined.
 * Submit -> Prepare -> Review -> Pending -> Payment -> Process -> Complete -> Draft
 */
export function getCurrentMilestone(
  payrun: PayrunListItem,
): PayrunSteps | 'Draft' | null {
  const stepMap: Record<MilestoneStepKey, number[]> = {
    ...StatusToStepMap(payrun.isInvoiceNeeded),
    [PayrunSteps.Complete]: [17, 18],
    Draft: [23, 24],
  };

  const match = (Object.keys(stepMap) as MilestoneStepKey[]).find((step) =>
    stepMap[step].includes(payrun.payrunStatusId),
  );

  return match ?? null;
}

/** Joins a tags array into a comma-separated string, or returns null when empty. */
export function formatTagsForDisplay(tags?: string[] | null): string | null {
  return tags?.length ? tags.join(', ') : null;
}

/** Absolute detail route for a pay run — XemploPay pay runs have their own detail view. */
export function getPayrunDetailPath(payrun: PayrunListItem): string {
  const path =
    payrun.payrollProcessingSystemId === PayrollProcessingSystemId.XemploPay
      ? PayrollDynamicRoutes.PayrunXemploPayById(payrun.id)
      : PayrollDynamicRoutes.PayrunById(payrun.id);
  return `/${path}`;
}
